import { Button, Drawer, useToast } from "@helmr/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { bulkFreeze, type BulkFreezeResult } from "../../api/plans";
import { Field, FormError, errorMessage, useArea, useIntentKey } from "./common";
import { t } from "./strings";
import s from "./members.module.css";

const B = t.drawer.bulk;

export interface PickedPlan { memberId: string; planId: string; name: string }

/** Gym closure: freeze many plans for the same dates. Names the plans picked in the list, or every active plan at a branch. */
export function BulkFreezeDrawer({ open, onOpenChange, picked, onDone }: { open: boolean; onOpenChange: (o: boolean) => void; picked: PickedPlan[]; onDone: () => void }) {
  const { client, ctx, today, session } = useArea();
  const qc = useQueryClient();
  const toast = useToast();
  const intent = useIntentKey();
  const branches = ctx.me?.branches ?? [];
  const [branchId, setBranchId] = useState(session.branchId ?? branches[0]?.id ?? "");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [reason, setReason] = useState("");
  const [scope, setScope] = useState<"all" | "picked">(picked.length > 0 ? "picked" : "all");
  const [problems, setProblems] = useState<Record<string, string>>({});
  const [result, setResult] = useState<BulkFreezeResult | null>(null);

  // The page clears its selection on success; keep the names so the result can still say who was not frozen.
  const nameRef = useRef(new Map<string, string>());
  if (picked.length > 0) nameRef.current = new Map(picked.map((p) => [p.planId, p.name]));
  const names = nameRef.current;
  const body = { branchId, from, to, reason: reason.trim(), ...(scope === "picked" ? { planIds: picked.map((p) => p.planId) } : {}) };
  const run = useMutation({
    mutationFn: () => bulkFreeze(client, body, { idempotencyKey: intent.keyFor(body) }),
    onSuccess: (r) => {
      intent.reset();
      setResult(r);
      onDone();
      void qc.invalidateQueries({ queryKey: ["members"] });
      void qc.invalidateQueries({ queryKey: ["member-plans"] });
      toast.show(B.result(r.applied, r.failed));
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!branchId) next.branch = t.add.branchRequired;
    if (!from) next.from = t.drawer.dateRequired;
    if (!to) next.to = t.drawer.dateRequired; else if (from && to < from) next.to = t.drawer.freeze.order;
    if (!reason.trim()) next.reason = t.drawer.reasonRequired;
    if (scope === "picked" && picked.length === 0) next.scope = B.noPicked;
    setProblems(next);
    if (Object.keys(next).length === 0) run.mutate();
  };

  return (
    <Drawer
      open={open} onOpenChange={onOpenChange} title={B.title} description={B.intro}
      footer={result ? (
        <Button variant="primary" onClick={() => onOpenChange(false)}>{t.drawer.close}</Button>
      ) : (
        <>
          <Button onClick={() => onOpenChange(false)}>{t.drawer.close}</Button>
          <Button type="submit" form="bulk-freeze-form" variant="primary" busy={run.isPending} busyLabel={t.drawer.working}>{B.confirm}</Button>
        </>
      )}
    >
      {result ? (
        <div className={s.form}>
          <div className={result.failed === 0 ? s.good : s.notice} role="status">{B.result(result.applied, result.failed)}</div>
          {result.outcomes.filter((o) => !o.ok).length > 0 ? (
            <>
              <h3 className={s.legend}>{B.failedHeading}</h3>
              <ul className={s.results}>
                {result.outcomes.filter((o) => !o.ok).map((o) => (
                  <li key={o.planId}><b>{names.get(o.planId) ?? o.planId}</b>: {o.error?.message}</li>
                ))}
              </ul>
            </>
          ) : null}
        </div>
      ) : (
        <form id="bulk-freeze-form" className={s.form} onSubmit={submit} noValidate>
          <Field id="bf-branch" label={B.branch} error={problems.branch}>
            <select id="bf-branch" className={s.select} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </Field>
          <Field id="bf-from" label={B.from} error={problems.from}>
            <input id="bf-from" type="date" className={s.input} value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field id="bf-to" label={B.to} error={problems.to}>
            <input id="bf-to" type="date" className={s.input} value={to} min={from} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Field id="bf-reason" label={B.reason} error={problems.reason}>
            <textarea id="bf-reason" className={s.textarea} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
          </Field>
          <fieldset className={s.radios}>
            <legend className={s.legend}>{B.scope}</legend>
            <label className={s.radio}><input type="radio" name="bf-scope" checked={scope === "all"} onChange={() => setScope("all")} />{B.scopeAll}</label>
            <label className={s.radio}><input type="radio" name="bf-scope" checked={scope === "picked"} disabled={picked.length === 0} onChange={() => setScope("picked")} />{B.scopePicked(picked.length)}</label>
            {problems.scope ? <span className={s.err} role="alert">{problems.scope}</span> : null}
          </fieldset>
          <FormError message={run.error ? errorMessage(run.error) : null} />
        </form>
      )}
    </Drawer>
  );
}
